import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	getTeacherByIdOptions,
	getTeacherByIdQueryKey,
	updateTeacherMutation,
	getAllTeachersQueryKey,
} from "@/lib/client/@tanstack/react-query.gen";
import type { TeacherWithCoursesDto } from "@/lib/client/types.gen";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
	ArrowLeft,
	Edit,
	Save,
	X,
	BookOpen,
	Calendar,
	Users,
	Eye,
	UserCog,
	GraduationCap,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "@tanstack/react-form";
import {
	getSubjectLabel,
	getLevelLabel,
	getSchoolLabel,
	getTimeSlot,
} from "@/stores/enum-store";
import type { ColumnDef } from "@tanstack/react-table";
import {
	DataTable,
	DataTableColumnHeader,
} from "@/components/data-table";

export const Route = createFileRoute("/admin/teachers/$id")({
	component: TeacherDetailsPage,
});

function TeacherDetailsPage() {
	const { id } = Route.useParams();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [isEditing, setIsEditing] = useState(false);

	const { data: teacher, isLoading: teacherLoading } = useQuery({
		...getTeacherByIdOptions({ path: { id } }),
	});

	const updateTeacherMut = useMutation({
		...updateTeacherMutation(),
		onSuccess: () => {
			toast.success("Professeur modifié avec succès");
			queryClient.invalidateQueries({
				queryKey: getTeacherByIdQueryKey({ path: { id } }),
			});
			queryClient.invalidateQueries({ queryKey: getAllTeachersQueryKey() });
			setIsEditing(false);
		},
		onError: () => {
			toast.error("Erreur lors de la modification du professeur");
		},
	});

	const form = useForm({
		defaultValues: {
			firstName: teacher?.firstName || "",
			lastName: teacher?.lastName || "",
		},
		onSubmit: async ({ value }) => {
			updateTeacherMut.mutate({
				path: { id },
				body: {
					firstName: value.firstName,
					lastName: value.lastName,
				},
			});
		},
	});

	const teacherCourses = teacher?.courses || [];

	const teacherStudents = useMemo(() => {
		const studentsMap = new Map<
			string,
			{ student: any; courses: string[] }
		>();

		for (const course of teacherCourses) {
			// Archived courses keep their past enrollments: not current students
			if (course.archivedAt) continue;
			const enrollments = course.enrollments || [];
			for (const enrollment of enrollments) {
				const student = enrollment.student;
				if (!student) continue;
				const existing = studentsMap.get(student.id);
				const courseLabel = `${getSubjectLabel(course.subject)} - ${getLevelLabel(course.level)}`;
				if (existing) {
					existing.courses.push(courseLabel);
				} else {
					studentsMap.set(student.id, {
						student,
						courses: [courseLabel],
					});
				}
			}
		}

		return Array.from(studentsMap.values());
	}, [teacherCourses]);

	const dayOfWeekLabels: Record<string, string> = {
		MONDAY: "Lundi",
		TUESDAY: "Mardi",
		WEDNESDAY: "Mercredi",
		THURSDAY: "Jeudi",
		FRIDAY: "Vendredi",
		SATURDAY: "Samedi",
		SUNDAY: "Dimanche",
	};

	const coursesColumns = useMemo<ColumnDef<any>[]>(
		() => [
			{
				accessorKey: "subject",
				meta: {
					label: "Matière",
					textValue: (row: any) => getSubjectLabel(row.subject),
				},
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Matière" />
				),
				cell: ({ row }) => {
					const course = row.original;
					return (
						<div className="flex items-center gap-2">
							<BookOpen className="h-4 w-4 text-blue-600" />
							<span className="font-medium">
								{getSubjectLabel(course.subject)}
							</span>
							{course.archivedAt && (
								<Badge variant="outline" className="text-xs text-gray-600 border-gray-300 bg-gray-100">
									Archivé
								</Badge>
							)}
						</div>
					);
				},
			},
			{
				accessorKey: "level",
				meta: {
					label: "Niveau",
					textValue: (row: any) => getLevelLabel(row.level),
				},
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Niveau" />
				),
				cell: ({ row }) => getLevelLabel(row.getValue("level")),
			},
			{
				accessorKey: "timeSlot",
				meta: {
					label: "Jour et horaire",
					textValue: (row: any) => {
						const ts = getTimeSlot(row.timeSlot);
						if (!ts) return "";
						return `${dayOfWeekLabels[ts.dayOfWeek || ""] || ts.dayOfWeek} ${ts.startTime} - ${ts.endTime}`;
					},
				},
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Jour et horaire" />
				),
				cell: ({ row }) => {
					const course = row.original;
					const timeSlot = getTimeSlot(course.timeSlot);
					return (
						<div className="flex items-center gap-2">
							<Calendar className="h-4 w-4 text-gray-400" />
							<span className="text-sm">
								{dayOfWeekLabels[timeSlot?.dayOfWeek || ""]}{" "}
								{timeSlot?.startTime} - {timeSlot?.endTime}
							</span>
						</div>
					);
				},
			},
			{
				accessorKey: "school",
				meta: {
					label: "École",
					textValue: (row: any) => getSchoolLabel(row.school),
				},
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="École" />
				),
				cell: ({ row }) => getSchoolLabel(row.getValue("school")),
			},
			{
				accessorKey: "enrollments",
				meta: {
					label: "Élèves inscrits",
					textValue: (row: any) => `${row.enrollments?.length || 0}`,
				},
				enableSorting: false,
				header: "Élèves inscrits",
				cell: ({ row }) => {
					const enrollmentCount = row.original.enrollments?.length || 0;
					return (
						<Badge variant="secondary">
							{enrollmentCount} élève{enrollmentCount > 1 ? "s" : ""}
						</Badge>
					);
				},
			},
			{
				id: "actions",
				meta: { label: "Actions" },
				enableSorting: false,
				enableHiding: false,
				header: "Actions",
				cell: ({ row }) => {
					const course = row.original;
					return (
						<Link
							to="/admin/courses/$id"
							params={{ id: course.id }}
						>
							<Button
								size="sm"
								variant="outline"
								title="Voir le cours"
							>
								<Eye className="h-3 w-3" />
							</Button>
						</Link>
					);
				},
			},
		],
		[dayOfWeekLabels],
	);

	const studentsColumns = useMemo<ColumnDef<{ student: any; courses: string[] }>[]>(
		() => [
			{
				id: "student",
				meta: { label: "Élève" },
				accessorFn: (row) => `${row.student.firstName} ${row.student.lastName}`,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Élève" />
				),
				cell: ({ row }) => {
					const { student } = row.original;
					return (
						<div className="flex items-center gap-2">
							<GraduationCap className="h-4 w-4 text-blue-600" />
							<span className="font-medium">
								{student.firstName} {student.lastName}
							</span>
						</div>
					);
				},
			},
			{
				id: "school",
				meta: { label: "École" },
				accessorFn: (row) => row.student.school,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="École" />
				),
				cell: ({ row }) => {
					const { student } = row.original;
					return (
						<span className="text-sm text-gray-600">
							{getSchoolLabel(student.school)}
						</span>
					);
				},
			},
			{
				id: "specialNeeds",
				meta: { label: "Besoins spécifiques" },
				enableSorting: false,
				header: "Besoins spécifiques",
				cell: ({ row }) => {
					const { student } = row.original;
					return (
						<div className="flex gap-1">
							{student.hasPap && (
								<Badge variant="secondary" className="text-xs">
									PAP
								</Badge>
							)}
							{student.hasPps && (
								<Badge variant="secondary" className="text-xs">
									PPS
								</Badge>
							)}
							{!student.hasPap && !student.hasPps && (
								<span className="text-gray-400 text-sm">-</span>
							)}
						</div>
					);
				},
			},
			{
				id: "courses",
				meta: { label: "Cours" },
				enableSorting: false,
				header: "Cours",
				cell: ({ row }) => {
					const { courses } = row.original;
					return (
						<div className="flex flex-wrap gap-1">
							{courses.map((courseLabel, i) => (
								<Badge
									key={i}
									variant="outline"
									className="text-xs"
								>
									{courseLabel}
								</Badge>
							))}
						</div>
					);
				},
			},
			{
				id: "actions",
				meta: { label: "Actions" },
				enableSorting: false,
				enableHiding: false,
				header: "Actions",
				cell: ({ row }) => {
					const { student } = row.original;
					return (
						<Link
							to="/admin/students/$id"
							params={{ id: student.id }}
						>
							<Button
								size="sm"
								variant="outline"
								title="Voir l'élève"
							>
								<Eye className="h-3 w-3" />
							</Button>
						</Link>
					);
				},
			},
		],
		[],
	);

	if (teacherLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
					<p className="mt-4 text-gray-600">
						Chargement du professeur...
					</p>
				</div>
			</div>
		);
	}

	if (!teacher) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<p className="text-gray-600">Professeur non trouvé</p>
					<Button
						variant="outline"
						onClick={() => navigate({ to: "/admin/teachers" })}
						className="mt-4"
					>
						<ArrowLeft className="h-4 w-4 mr-2" />
						Retour
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center gap-4">
				<Button
					variant="ghost"
					size="sm"
					onClick={() => navigate({ to: "/admin/teachers" })}
				>
					<ArrowLeft className="h-4 w-4 mr-2" />
					Retour
				</Button>
			</div>

			<div className="flex justify-between items-start">
				<div>
					<h1 className="text-3xl font-bold">
						{teacher.firstName} {teacher.lastName}
					</h1>
					<p className="text-gray-600 mt-2">
						{teacherCourses.length} cours -{" "}
						{teacherStudents.length} élève
						{teacherStudents.length > 1 ? "s" : ""}
					</p>
				</div>
				{!isEditing ? (
					<Button onClick={() => setIsEditing(true)}>
						<Edit className="h-4 w-4 mr-2" />
						Modifier
					</Button>
				) : (
					<div className="flex gap-2">
						<Button
							variant="outline"
							onClick={() => setIsEditing(false)}
						>
							<X className="h-4 w-4 mr-2" />
							Annuler
						</Button>
						<Button
							onClick={() => form.handleSubmit()}
							disabled={updateTeacherMut.isPending}
						>
							<Save className="h-4 w-4 mr-2" />
							{updateTeacherMut.isPending
								? "Enregistrement..."
								: "Enregistrer"}
						</Button>
					</div>
				)}
			</div>

			{/* Teacher Info Card */}
			<Card>
				<CardHeader>
					<CardTitle>Informations du professeur</CardTitle>
					<CardDescription>
						Détails et informations personnelles
					</CardDescription>
				</CardHeader>
				<CardContent>
					{!isEditing ? (
						<div className="grid grid-cols-2 gap-6">
							<div className="space-y-4">
								<div>
									<Label className="text-gray-500">
										Prénom
									</Label>
									<p className="font-medium">
										{teacher.firstName}
									</p>
								</div>
								<div>
									<Label className="text-gray-500">Nom</Label>
									<p className="font-medium">
										{teacher.lastName}
									</p>
								</div>
							</div>
							<div className="space-y-4">
								<div>
									<Label className="text-gray-500">
										Nom complet
									</Label>
									<div className="flex items-center gap-2">
										<UserCog className="h-4 w-4 text-gray-400" />
										<p className="font-medium">
											{teacher.fullName}
										</p>
									</div>
								</div>
								<div>
									<Label className="text-gray-500">
										Date de création
									</Label>
									<p className="font-medium">
										{teacher.createdAt
											? new Date(
													teacher.createdAt,
												).toLocaleDateString("fr-FR")
											: "-"}
									</p>
								</div>
							</div>
						</div>
					) : (
						<form className="space-y-4">
							<div className="grid grid-cols-2 gap-4">
								<form.Field
									name="firstName"
									children={(field) => (
										<div>
											<Label htmlFor={field.name}>
												Prénom
											</Label>
											<Input
												id={field.name}
												value={field.state.value}
												onChange={(e) =>
													field.handleChange(
														e.target.value,
													)
												}
											/>
										</div>
									)}
								/>
								<form.Field
									name="lastName"
									children={(field) => (
										<div>
											<Label htmlFor={field.name}>
												Nom
											</Label>
											<Input
												id={field.name}
												value={field.state.value}
												onChange={(e) =>
													field.handleChange(
														e.target.value,
													)
												}
											/>
										</div>
									)}
								/>
							</div>
						</form>
					)}
				</CardContent>
			</Card>

			{/* Courses Card */}
			<Card>
				<CardHeader>
					<CardTitle>
						Cours ({teacherCourses.length})
					</CardTitle>
					<CardDescription>
						Liste des cours dispensés par ce professeur
					</CardDescription>
				</CardHeader>
				<CardContent>
					<DataTable
						columns={coursesColumns}
						data={teacherCourses}
						emptyMessage="Aucun cours attribué"
					/>
				</CardContent>
			</Card>

			{/* Students Card */}
			<Card>
				<CardHeader>
					<CardTitle>
						Élèves ({teacherStudents.length})
					</CardTitle>
					<CardDescription>
						Liste de tous les élèves de ce professeur
					</CardDescription>
				</CardHeader>
				<CardContent>
					<DataTable
						columns={studentsColumns}
						data={teacherStudents}
						emptyMessage="Aucun élève"
					/>
				</CardContent>
			</Card>
		</div>
	);
}
